import "./Sidebar.scss";
import {CSSProperties, ReactNode} from "react";
import {Link, useLocation} from 'react-router-dom';
import {PATHS_API} from "../../../../../enum";
import {PATHS} from "../../../../routers/utils/Paths.links";
import {useUserStore} from "../../../../../modules/shop/user/store";
import {useHasPersonalProducts} from "../../../../hooks/useHasPersonalProducts";
import {getLevel, LEVEL_NAMES} from "../../ChildPages/PersonalBonus/components/LevelBlock/LevelBlock";

export enum CHILD_PROFILE_PATHS {
    BASKET = '/basket',
    PERSONAL_BONUS = '/personal-bonus',
    PERSONAL_PRODUCTS = '/personal-products',
    TRANSFER = '/transfer',
    PAYMENTS = '/payments',
    REFERRAL_PROGRAM = '/referral',
    BONUS_CODE = '/bonus-code',
    INDIVIDUAL_SET = '/custom-set',
    ACCOUNT = '/account',
    STATS = '/stats',
    ADMIN_PANEL = '/admin-panel',
}

export interface IProfileMenuItem {
    to: CHILD_PROFILE_PATHS,
    text: string,
    // колір плитки з іконкою
    accent: string,
    icon: ReactNode,
}

// Іконки в одному стилі: контур 2px, заокруглені кінці (малюються всередині <svg> з stroke=currentColor)
const ICONS = {
    basket: <><path d="M5.5 8h13l-1 12h-11z" /><path d="M9 8a3 3 0 016 0" /><path d="M9.5 12.5v3M14.5 12.5v3" /></>,
    payments: <><rect x="2.5" y="5" width="19" height="14" rx="3" /><path d="M2.5 10h19" /><path d="M6.5 15h4" /></>,
    bonus: <><path d="M12 3.5l2.5 5 5.5.8-4 3.9 1 5.5-5-2.6-5 2.6 1-5.5-4-3.9 5.5-.8z" /></>,
    products: <><path d="M6.5 3.5h11l3.5 5.5-9 11.5L3 9z" /><path d="M3 9h18" /><path d="M9.5 9L12 20.5 14.5 9 12 3.5z" /></>,
    transfer: <><path d="M4 8.5h13.5L14 5" /><path d="M20 15.5H6.5L10 19" /></>,
    bonusCode: <><path d="M3 8.5a2 2 0 002-2h14a2 2 0 002 2v1.7a1.8 1.8 0 000 3.6v1.7a2 2 0 00-2 2H5a2 2 0 00-2-2v-1.7a1.8 1.8 0 000-3.6z" /><path d="M14.5 6.5v11" strokeDasharray="1.6 2.4" /></>,
    referral: <><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0113 0" /><path d="M16 4.5a3.5 3.5 0 010 7M18 14.5a6.5 6.5 0 013.5 5.5" /></>,
    stats: <><path d="M4 20V10M10 20V4M16 20v-7M21 20H3" /></>,
    account: <><circle cx="12" cy="8" r="4" /><path d="M4 20.5a8 8 0 0116 0" /></>,
    logout: <><path d="M14.5 4H18a2 2 0 012 2v12a2 2 0 01-2 2h-3.5" /><path d="M10 16l-4-4 4-4" /><path d="M6 12h10" /></>,
};

export const dataForProfile: IProfileMenuItem[] = [
    { to: CHILD_PROFILE_PATHS.BASKET, text: "Кошик", accent: "#3579f6", icon: ICONS.basket },
    { to: CHILD_PROFILE_PATHS.PAYMENTS, text: "Платежі", accent: "#3fcf6b", icon: ICONS.payments },
    { to: CHILD_PROFILE_PATHS.PERSONAL_BONUS, text: "Особистий бонус", accent: "#ffb52e", icon: ICONS.bonus },
    { to: CHILD_PROFILE_PATHS.PERSONAL_PRODUCTS, text: "Особисті товари", accent: "#a764f5", icon: ICONS.products },
    // Реферальна програма прихована з меню (ПК і бургер), сторінка /profile/referral лишилась. Щоб повернути — розкоментуйте.
    // { to: CHILD_PROFILE_PATHS.REFERRAL_PROGRAM, text: "Реферальна програма", accent: "#ff5fa2", icon: ICONS.referral },
    { to: CHILD_PROFILE_PATHS.TRANSFER, text: "Переказ балансу", accent: "#1ab0ff", icon: ICONS.transfer },
    { to: CHILD_PROFILE_PATHS.BONUS_CODE, text: "Бонус код", accent: "#ff7a45", icon: ICONS.bonusCode },
]

export const statsMenuItem: IProfileMenuItem = { to: CHILD_PROFILE_PATHS.STATS, text: "Статистика", accent: "#ef476f", icon: ICONS.stats };

export const dataForProfileAccount: IProfileMenuItem[] = [
    statsMenuItem,
    { to: CHILD_PROFILE_PATHS.ACCOUNT, text: "Про обліковий запис", accent: "#8a94a6", icon: ICONS.account },
]

// Іконка пункту меню (монохромна, колір береться з тексту)
export const MenuIcon = ({ item, size = 18 }: { item: Pick<IProfileMenuItem, "accent" | "icon">, size?: number }) => (
    <span className="sb-tile" style={{ "--accent": item.accent } as CSSProperties} aria-hidden="true">
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {item.icon}
        </svg>
    </span>
);

const LEVEL_STEP = 2500; // стільки ₴ поповнень відкривають наступний рівень
const RING_R = 27;
const RING_C = 2 * Math.PI * RING_R;

// Картка гравця вгорі меню: аватар у кільці прогресу до наступного рівня, рівень і баланс
const PlayerCard = () => {
    const user = useUserStore(state => state.user)
    const level = getLevel(user ?? {})
    const maxed = (Number(user?.personalBonus) || 0) >= 5
    const progress = maxed ? 1 : ((Number(user?.currentAmount) || 0) % LEVEL_STEP) / LEVEL_STEP

    return (
        <div className={`sb-player ${maxed ? "is-max" : ""}`}>
            <Link className="sb-avatar" to={PATHS.PROFILE + CHILD_PROFILE_PATHS.PERSONAL_BONUS} title={`Рівень ${level}: ${LEVEL_NAMES[level]}`}>
                <svg className="sb-ring" viewBox="0 0 64 64" aria-hidden="true">
                    <circle cx="32" cy="32" r={RING_R} className="sb-ring-track" />
                    <circle cx="32" cy="32" r={RING_R} className="sb-ring-arc"
                            strokeDasharray={`${RING_C * progress} ${RING_C}`} transform="rotate(-90 32 32)" />
                </svg>
                { user?.avatarUrl
                    ? <img src={user.avatarUrl} alt="" />
                    : <span className="sb-avatar-empty">{user?.personaName?.[0]?.toUpperCase() ?? "?"}</span>
                }
                <span className="sb-level">{level}</span>
            </Link>
            <div className="sb-player-info">
                <b className="sb-name" title={user?.personaName}>{user?.personaName}</b>
                <span className="sb-level-name">{LEVEL_NAMES[level]} · бонус +{Number(user?.personalBonus) || 0}%</span>
                <Link className="sb-balance" to={PATHS.DEPOSIT} title="Поповнити баланс">
                    <span>{Number(user?.balance ?? 0).toFixed(2)} ₴</span>
                    <i aria-hidden="true">+</i>
                </Link>
            </div>
        </div>
    );
};

const MenuLink = ({ item, active }: { item: IProfileMenuItem, active: boolean }) => (
    <Link
        to={PATHS.PROFILE + item.to}
        className={`sb-item ${active ? "is-active" : ""}`}
        style={{ "--accent": item.accent } as CSSProperties}
        aria-current={active ? "page" : undefined}
        title={item.text}
    >
        <MenuIcon item={item} size={22} />
        <span className="sb-text">{item.text}</span>
    </Link>
);

const Sidebar = () => {
    const location = useLocation();
    const hasPersonalProducts = useHasPersonalProducts();

    const visibleProfileItems = dataForProfile.filter((item) =>
        item.to !== CHILD_PROFILE_PATHS.PERSONAL_PRODUCTS || hasPersonalProducts
    );
    const isActive = (item: IProfileMenuItem) => location.pathname === PATHS.PROFILE + item.to;

    return (
        <nav className="sidebar" aria-label="Меню профілю">
            <PlayerCard />

            <h3 className="sidebar-title">Загальне</h3>
            { visibleProfileItems.map((item) => <MenuLink key={item.to} item={item} active={isActive(item)} />) }

            <h3 className="sidebar-title">Обліковий запис</h3>
            { dataForProfileAccount.map((item) => <MenuLink key={item.to} item={item} active={isActive(item)} />) }

            <a className="sb-item sb-logout" style={{ "--accent": "#f73737" } as CSSProperties} onClick={() => localStorage.removeItem("isAuth")} href={PATHS_API.LOGOUT} title="Вийти з облікового запису">
                <MenuIcon item={{ accent: "#f73737", icon: ICONS.logout }} size={22} />
                <span className="sb-text">Вийти</span>
            </a>
        </nav>
    );
};

export default Sidebar;
